import { Chart } from 'primereact/chart';
import React, { useEffect, useState } from 'react';

import {
    useGetTeamDailyMetricViewQuery,
    useGetTeamMonthlyMetricViewQuery,
} from '../../pages/metricdashboard/metricDashboardSlice';
import { Numbers, metricType } from '../../utils/typeConstants';
import MetricProgressDirection from '../metricProgressDirection/metricProgressDirection';

type TeamMetricViewProps = {
    scorecardVersionId: number;
    employeeNumber: string;
    metricId: number;
    metricName: string;
    month: number;
    year: number;
    resultDate: Date | '';
    metricDirectionId: number;
};

export const TeamMetricView = ({
    employeeNumber,
    month,
    year,
    metricId,
    metricName,
    scorecardVersionId,
    resultDate,
    metricDirectionId,
}: TeamMetricViewProps) => {
    const [basicData, setBasicData] = useState<any>();
    const [scorecardTarget, setScorecardTarget] = useState<any>('');
    const [scoreType, setScoreType] = useState<any>('');
    const { data: teamMonthlyMetric } = useGetTeamMonthlyMetricViewQuery(
        {
            employeeNumber,
            year,
            month,
            scorecardVersionId,
            metricId,
        },
        { skip: !employeeNumber || year === 0 || month === 0 || scorecardVersionId === 0 || metricId === 0 }
    );

    const { data: teamDailyMetric } = useGetTeamDailyMetricViewQuery(
        {
            employeeNumber,
            scorecardVersionId,
            metricId,
            resultDate,
        },
        { skip: !employeeNumber || scorecardVersionId === 0 || metricId === 0 || !resultDate }
    );

    const basicOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        aspectRatio: 0.7,
        responsive: true,
        plugins: {
            legend: {
                display: true,
                labels: {
                    filter: (legendItem: any, chartData: any) => {
                        return chartData.datasets[legendItem.datasetIndex].label !== 'Metric Score';
                    },
                },
            },
            tooltip: {
                callbacks: {
                    label: (tooltipItem: any) => {
                        const dataset = tooltipItem.dataset;
                        const index = tooltipItem.dataIndex;
                        const value = dataset.data[index];
                        const score =
                            dataset?.metricType?.toLowerCase() === metricType.PERCENTAGE
                                ? value + '%'
                                : value;
                        return [`${dataset.label}: ${score}`];
                    },
                },
            },
        },
        scales: {
            x: {
                display: true,
                grid: {
                    display: false,
                },
            },
            y: {
                display: true,
                position: 'left',
                grid: {
                    display: false,
                },
                title: {
                    display: true,
                    font: {
                        size: 14,
                        weight: 'bold',
                    },
                },
            },
        },
    };

    // useEffect(() => {
    //     return () => {
    //         // Cleanup: Destroy the chart instance on unmount
    //         if (chartRef.current) {
    //             chartRef.current.chart.destroy();
    //         }
    //     };
    // }, []);

    useEffect(() => {
        if (teamMonthlyMetric?.data.length > 0) {
            const st =
                teamMonthlyMetric?.data[0].metric_type_name?.toLowerCase() === metricType.PERCENTAGE
                    ? (Number(teamMonthlyMetric?.data[0].scorecard_target ?? 0) * Numbers.HUNDRED).toFixed(2)
                    : Number(teamMonthlyMetric?.data[0].scorecard_target ?? 0).toFixed(2);
            setScorecardTarget(st);
            const stype =
                teamMonthlyMetric?.data[0].metric_type_name?.toLowerCase() === metricType.PERCENTAGE
                    ? '%'
                    : '';
            setScoreType(stype);
            const metricScore = teamMonthlyMetric?.data?.map((item: any) => {
                return item.metric_type_name?.toLowerCase() === metricType.PERCENTAGE
                    ? (Number(item.metric_value ?? 0) * Numbers.HUNDRED).toFixed(2)
                    : Number(item.metric_value ?? 0).toFixed(2);
            });
            const metricTargetScore = teamMonthlyMetric?.data?.map((item: any) => {
                return item.metric_type_name?.toLowerCase() === metricType.PERCENTAGE
                    ? (Number(item.target ?? 0) * Numbers.HUNDRED).toFixed(2)
                    : Number(item.target ?? 0).toFixed(2);
            });

            const data = {
                labels: teamMonthlyMetric?.data?.map((item: any) => item.employee_name),
                datasets: [
                    {
                        data: metricTargetScore,
                        type: 'line',
                        label: 'Individual Target',
                        borderColor: '#132F51',
                        backgroundColor: '#ffffff',
                        borderDash: [5, 5],
                        borderWidth: Numbers.TWO,
                        pointRadius: 2,
                        fill: false,
                        metricType: teamMonthlyMetric?.data[0]?.metric_type_name,
                    },
                    {
                        data: metricScore,
                        fill: true,
                        label: 'Metric Score',
                        backgroundColor: '#3299FF',
                        borderWidth: Numbers.ONE,
                        borderRadius: Numbers.FIVE,
                        metricType: teamMonthlyMetric?.data[0]?.metric_type_name,
                    },
                ],
            };
            setBasicData(data);
        } else setBasicData([]);
    }, [teamMonthlyMetric]);

    useEffect(() => {
        if (teamDailyMetric?.data.length > 0) {
            const st =
                teamDailyMetric?.data[0].metric_type_name?.toLowerCase() === metricType.PERCENTAGE
                    ? (Number(teamDailyMetric?.data[0].scorecard_target ?? 0) * Numbers.HUNDRED).toFixed(2)
                    : Number(teamDailyMetric?.data[0].scorecard_target ?? 0).toFixed(2);
            setScorecardTarget(st);
            const stype =
                teamDailyMetric?.data[0].metric_type_name?.toLowerCase() === metricType.PERCENTAGE ? '%' : '';
            setScoreType(stype);
            const metricScore = teamDailyMetric?.data?.map((item: any) => {
                return item.metric_type_name?.toLowerCase() === metricType.PERCENTAGE
                    ? (Number(item.metric_value ?? 0) * Numbers.HUNDRED).toFixed(2)
                    : Number(item.metric_value ?? 0).toFixed(2);
            });
            const metricTargetScore = teamDailyMetric?.data?.map((item: any) => {
                return item.metric_type_name?.toLowerCase() === metricType.PERCENTAGE
                    ? (Number(item.target ?? 0) * Numbers.HUNDRED).toFixed(2)
                    : Number(item.target ?? 0).toFixed(2);
            });

            const data = {
                labels: teamDailyMetric?.data?.map((item: any) => item.employee_name),
                datasets: [
                    {
                        data: metricTargetScore,
                        type: 'line',
                        label: 'Individual Target',
                        borderColor: '#104A15',
                        backgroundColor: '#ffffff',
                        borderDash: [5, 5],
                        borderWidth: Numbers.TWO,
                        pointRadius: 2,
                        fill: false,
                        metricType: teamDailyMetric?.data[0]?.metric_type_name,
                    },
                    {
                        data: metricScore,
                        fill: true,
                        label: 'Metric Score',
                        backgroundColor: '#64D96E',
                        borderWidth: Numbers.ONE,
                        borderRadius: Numbers.FIVE,
                        metricType: teamDailyMetric?.data[0]?.metric_type_name,
                    },
                ],
            };
            setBasicData(data);
        } else setBasicData([]);
    }, [teamDailyMetric]);

    return (
        <div className="row mt-2">
            <div className="row">
                <div className="col-2 align-top">
                    <h6 className="card-title metricName mb-0 align-top">{metricName}</h6>
                </div>
                <div className="col-3">
                    <span className="me-2">Scorecard Target</span>
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-2" />
                <div className="col-3">
                    <MetricProgressDirection
                        metricDirectionId={metricDirectionId}
                        trendPercentage={scorecardTarget}
                        sType={scoreType}
                    />
                </div>
            </div>
            <div className="row card">
                <Chart type="bar" data={basicData} options={basicOptions} />
            </div>
        </div>
    );
};
